import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

type SectionId = 'hero' | 'categories' | 'hotcollection' | 'deals' | 'newarrivals' | 'portfolio' | 'about' | 'services' | 'contact';

interface Section {
  id: SectionId;
  enabled: boolean;
  order: number;
}

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const SECTION_INFO: Record<SectionId, { name: string; icon: string; desc: string; color: string }> = {
  hero: { name: 'Hero Banner', icon: 'ri-image-line', desc: 'Main banner with featured products', color: 'bg-rose-500' },
  categories: { name: 'Category Chips', icon: 'ri-apps-line', desc: 'Quick category filter buttons', color: 'bg-amber-500' },
  hotcollection: { name: 'Hot Collection', icon: 'ri-fire-line', desc: 'Trending products with layout', color: 'bg-orange-500' },
  deals: { name: "Today's Deals", icon: 'ri-flashlight-line', desc: 'Special discount offers', color: 'bg-red-500' },
  newarrivals: { name: 'New Arrivals', icon: 'ri-sparkling-line', desc: 'Fresh new products', color: 'bg-emerald-500' },
  portfolio: { name: 'Product Portfolio', icon: 'ri-shopping-bag-3-line', desc: 'Full product catalog grid', color: 'bg-violet-500' },
  about: { name: 'About Us', icon: 'ri-store-2-line', desc: 'Brand story section', color: 'bg-blue-500' },
  services: { name: 'Services', icon: 'ri-customer-service-2-line', desc: 'Features & benefits', color: 'bg-cyan-500' },
  contact: { name: 'Contact', icon: 'ri-phone-line', desc: 'Contact info & form', color: 'bg-teal-500' },
};

export default function HomeLayoutPanel({ onToast }: Props) {
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState<SectionId | null>(null);

  useEffect(() => {
    loadLayout();
  }, []);

  const loadLayout = async () => {
    const { data, error } = await supabase
      .from('home_layout')
      .select('sections')
      .eq('id', 'main')
      .maybeSingle();
    
    if (error) {
      onToast('Layout load nahi hua: ' + error.message, 'error');
      setLoading(false);
      return;
    }

    if (data?.sections) {
      setSections(data.sections as Section[]);
    } else {
      // Default layout
      setSections([
        { id: 'hero', enabled: true, order: 1 },
        { id: 'categories', enabled: true, order: 2 },
        { id: 'hotcollection', enabled: true, order: 3 },
        { id: 'deals', enabled: true, order: 4 },
        { id: 'newarrivals', enabled: true, order: 5 },
        { id: 'portfolio', enabled: true, order: 6 },
        { id: 'about', enabled: true, order: 7 },
        { id: 'services', enabled: true, order: 8 },
        { id: 'contact', enabled: true, order: 9 },
      ]);
    }
    setLoading(false);
  };

  const saveLayout = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('home_layout')
      .upsert({
        id: 'main',
        sections: sections,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      onToast('Save nahi hua: ' + error.message, 'error');
    } else {
      onToast('Home page layout saved! Home page refresh karo.', 'success');
    }
    setSaving(false);
  };

  const toggleSection = (id: SectionId) => {
    setSections(prev => prev.map(s => 
      s.id === id ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    setSections(prev => {
      const newSections = [...prev];
      [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
      return newSections.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const moveDown = (index: number) => {
    if (index === sections.length - 1) return;
    setSections(prev => {
      const newSections = [...prev];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      return newSections.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleDragStart = (id: SectionId) => {
    setDraggingId(id);
  };

  const handleDragOver = (e: React.DragEvent, targetId: SectionId) => {
    e.preventDefault();
    if (draggingId === targetId) return;
    
    setSections(prev => {
      const fromIndex = prev.findIndex(s => s.id === draggingId);
      const toIndex = prev.findIndex(s => s.id === targetId);
      if (fromIndex === -1 || toIndex === -1) return prev;
      
      const newSections = [...prev];
      const [moved] = newSections.splice(fromIndex, 1);
      newSections.splice(toIndex, 0, moved);
      return newSections.map((s, i) => ({ ...s, order: i + 1 }));
    });
  };

  const handleDragEnd = () => {
    setDraggingId(null);
  };

  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
            <i className="ri-layout-3-line text-violet-600 text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Home Page Layout</p>
            <p className="text-gray-400 text-xs">Sections ka order aur visibility control karo</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>
        </div>
        <div>
          <p className="text-amber-800 font-bold text-sm">Kaise use karein?</p>
          <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
            • <strong>Drag & Drop</strong> — sections ko upar neeche kheencho<br/>
            • <strong>Toggle</strong> — section on/off karo<br/>
            • <strong>Save</strong> — changes home page pe live ho jaayenge
          </p>
        </div>
      </div>

      {/* Sections List */}
      <div className="space-y-2">
        {sortedSections.map((section, index) => {
          const info = SECTION_INFO[section.id];
          const isDragging = draggingId === section.id;
          
          return (
            <div
              key={section.id}
              draggable
              onDragStart={() => handleDragStart(section.id)}
              onDragOver={(e) => handleDragOver(e, section.id)}
              onDragEnd={handleDragEnd}
              className={`bg-white rounded-2xl border-2 p-4 transition-all cursor-move ${
                isDragging ? 'border-violet-500 shadow-lg scale-[1.02]' : 'border-gray-100'
              } ${!section.enabled ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3">
                {/* Drag handle */}
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                  <i className="ri-drag-move-line"></i>
                </div>

                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl ${info.color} flex items-center justify-center shrink-0`}>
                  <i className={`${info.icon} text-white text-lg`}></i>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-gray-900 font-bold text-sm">{info.name}</p>
                    <span className="text-gray-400 text-[10px] font-bold bg-gray-100 px-2 py-0.5 rounded-full">
                      #{section.order}
                    </span>
                    {!section.enabled && (
                      <span className="text-gray-500 text-[10px] font-bold bg-gray-100 px-2 py-0.5 rounded-full">
                        HIDDEN
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs truncate">{info.desc}</p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-up-line"></i>
                  </button>
                  <button
                    onClick={() => moveDown(index)}
                    disabled={index === sections.length - 1}
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-down-line"></i>
                  </button>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`w-12 h-7 rounded-full flex items-center transition-all cursor-pointer ${
                      section.enabled ? 'bg-green-500 justify-end pr-1' : 'bg-gray-300 justify-start pl-1'
                    }`}
                  >
                    <div className="w-5 h-5 rounded-full bg-white shadow-sm"></div>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save Button */}
      <button
        onClick={saveLayout}
        disabled={saving}
        className="w-full py-4 rounded-2xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
      >
        {saving ? (
          <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
        ) : (
          <><i className="ri-save-line"></i>Save Layout Changes</>
        )}
      </button>

      {/* Preview */}
      <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4">
        <p className="text-violet-800 font-bold text-sm mb-3 flex items-center gap-2">
          <i className="ri-eye-line"></i> Current Layout Preview
        </p>
        <div className="space-y-1.5">
          {sortedSections.filter(s => s.enabled).map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 text-xs">
              <span className="w-5 h-5 rounded-full bg-violet-500 text-white flex items-center justify-center text-[10px] font-bold">
                {i + 1}
              </span>
              <span className="text-violet-700">{SECTION_INFO[s.id].name}</span>
            </div>
          ))}
        </div>
        <p className="text-violet-600 text-xs mt-3">
          {sortedSections.filter(s => !s.enabled).length} sections hidden
        </p>
      </div>
    </div>
  );
}